import { useTranslation } from 'react-i18next';
import { SxProp, View } from 'dripsy';
import React, { useContext } from 'react';

import { ModalView } from '@/design-system/components/modal/ModalView';
import { useModalView } from '@/design-system/components/modal/useModalView.hook';
import { H6, TextBody, TextCaption } from '@/design-system/components/typography';
import { Divider } from '@/design-system/components/Divider';
import { CurrenciesIcon } from '@/features/earn/components';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { formatDate } from '@/utils/dateFormatting';
import { ActivityHistoryDetailsProps } from '../../screens/ActivityHistory/tabs/ActivityHistoryDetails.modal.web';
import { CurrenciesContext } from '@/currencies/currency.context';
import { ModalHeader } from '@/design-system/components/modal';
import { ActivityActionTag } from '../activityHistory/ActivityActionTag';
import { getEarnTenorTranslationKey } from '../../utils/getEarnTenorTranslationKey';

export const ActivityHistoryDetailsWeb: React.FC<ActivityHistoryDetailsProps> = ({ subscription }) => {
  const { t } = useTranslation('earn');
  const { getDecimalByAsset } = useContext(CurrenciesContext);
  const { useModalProps, closeModal } = useModalView({});

  const tenor = getEarnTenorTranslationKey(subscription.tenor);

  return (
    <ModalView useModalProps={useModalProps}>
      <View sx={styles.container}>
        <ModalHeader title={t('earn-invest::glossary.activity-details')} closeModal={closeModal} />
        <Divider />
        <View sx={styles.modalBody}>
          <ModalBodyRow sx={{ mb: '$7' }}>
            <CurrenciesIcon asset={subscription.asset} subText={subscription.category} subTextSx={{ color: '$textPrimary' }} />
          </ModalBodyRow>
          <ModalBodyRow sx={{ mb: '$5' }}>
            <View sx={styles.amountBox}>
              <TextCaption semiBold secondary>
                {t('earn-invest::glossary.est-apy')}
              </TextCaption>
              <H6 bold>
                {formatAmountByDecimals({
                  value: subscription.estimatedApyPercent,
                  decimals: 2,
                  suffix: '%'
                })}
              </H6>
            </View>
            <View sx={{ ...styles.amountBox, alignItems: 'flex-end' }}>
              <TextCaption secondary semiBold>
                {t('earn-invest::glossary.tenor')}
              </TextCaption>
              <H6 bold>{t(tenor.key, tenor.options)}</H6>
            </View>
          </ModalBodyRow>
          <ModalBodyRow sx={{ mb: '$5' }}>
            <View sx={styles.amountBox}>
              <TextCaption semiBold secondary>
                {t('earn-invest::glossary.principle')}
              </TextCaption>
              <H6 bold sx={{ color: '$secondary' }}>
                {formatAmountByDecimals({
                  value: subscription.amount,
                  decimals: getDecimalByAsset(subscription.asset),
                  suffix: subscription.asset
                })}
              </H6>
            </View>
          </ModalBodyRow>
          <ModalBodyDataRow sx={{ mb: '$3' }} label={t('earn-invest::glossary.action')}>
            <ActivityActionTag action={subscription.type} />
          </ModalBodyDataRow>
          <ModalBodyDataRow sx={{ mb: '$3' }} label={t('earn-invest::glossary.date')} value={formatDate(subscription.subscriptionDate)} />
          {subscription.maturityDate && (
            <ModalBodyDataRow sx={{ mb: '$3' }} label={t('earn-invest::glossary.maturity')} value={formatDate(subscription.maturityDate)} />
          )}
          <ModalBodyDataRow label={t('earn-invest::glossary.ref-id')} value={subscription.refId} />
        </View>
      </View>
    </ModalView>
  );
};

const ModalBodyRow: React.FC<{ sx?: SxProp }> = ({ sx, children }) => {
  return <View sx={{ ...styles.modalBodyRow, ...sx }}>{children}</View>;
};

const ModalBodyDataRow: React.FC<{ sx?: SxProp; label: string; value?: string }> = ({ label, value, sx, children }) => {
  return (
    <ModalBodyRow sx={{ alignItems: 'center', ...sx }}>
      <TextBody secondary>{label}</TextBody>
      {!!value && <TextBody>{value}</TextBody>}
      {!!children && children}
    </ModalBodyRow>
  );
};

enum StyleName {
  container = 'container',
  modalHeader = 'modalHeader',
  modalBody = 'modalBody',
  modalBodyRow = 'modalBodyRow',
  amountBox = 'amountBox'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    width: 500
  },
  modalHeader: {
    flex: 1,
    padding: '$7',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  modalBody: {
    padding: '$7'
  },
  modalBodyRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  amountBox: {}
};
